import { CheckCircleOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Progress,
  Radio,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatDateTime, orDash } from '../../lib/format';
import { getDelivery, handover, recordPdi, type HandoverAckType, type PdiItem, type PdiResult } from './api';

const { Text } = Typography;

const RESULT_TONE: Record<PdiResult, string> = {
  PASS: 'success',
  FAIL: 'error',
  NA: 'default',
};

export function DeliveryPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const [noting, setNoting] = useState<{ item: PdiItem; result: PdiResult } | null>(null);
  const [note, setNote] = useState('');
  const [handingOver, setHandingOver] = useState(false);
  const [ackType, setAckType] = useState<HandoverAckType>('SIGNATURE');
  const [ackRef, setAckRef] = useState('');
  const [odometer, setOdometer] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['delivery', uid],
    queryFn: () => getDelivery(uid),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery', uid] });
    void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    void queryClient.invalidateQueries({ queryKey: ['units'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const check = useMutation({
    mutationFn: (input: { itemUid: string; result: PdiResult; note?: string }) =>
      recordPdi(input.itemUid, {
        result: input.result,
        ...(input.note !== undefined ? { note: input.note } : {}),
      }),
    onSuccess: () => {
      refresh();
      setNoting(null);
    },
    onError: (e: unknown) => {
      fail(e, t('delivery.checkFailed'));
    },
  });

  const hand = useMutation({
    mutationFn: () =>
      handover(uid, {
        handoverAckType: ackType,
        handoverAckRef: ackRef.trim(),
        ...(odometer !== null ? { odometer } : {}),
      }),
    onSuccess: () => {
      refresh();
      setHandingOver(false);
      void message.success(t('delivery.handedOver'));
    },
    onError: (e: unknown) => {
      fail(e, t('delivery.handoverFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  const done = data.pdiItems.length - data.pdiOutstanding.length;
  const total = data.pdiItems.length;
  const delivered = data.status === 'DELIVERED';

  /** PASS goes straight through; FAIL and NA need a note first. */
  const record = (item: PdiItem, result: PdiResult): void => {
    if (result === 'PASS') {
      check.mutate({ itemUid: item.uid, result });
      return;
    }
    setNote(item.note ?? '');
    setNoting({ item, result });
  };

  return (
    <>
      <DetailPage
        title={data.customerName}
        subtitle={`${data.doNo} · ${data.chassisNo}`}
        tags={[
          {
            label: t(`delivery.status.${data.status}`),
            colour: delivered ? 'success' : data.status === 'READY' ? 'processing' : undefined,
          },
          ...(data.pdiFailed.length > 0
            ? [{ label: t('delivery.failedCount', { count: data.pdiFailed.length }), colour: 'error' }]
            : []),
        ]}
        onBack={() => {
          void navigate('/deliveries');
        }}
        actions={
          delivered ? null : (
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              disabled={data.pdiOutstanding.length > 0}
              onClick={() => {
                setAckRef('');
                setOdometer(null);
                setHandingOver(true);
              }}
            >
              {t('delivery.handover')}
            </Button>
          )
        }
        tabs={[
          {
            key: 'pdi',
            label: t('delivery.tabs.pdi'),
            content: (
              <Flex vertical gap={16}>
                {delivered ? (
                  <Alert
                    type="success"
                    showIcon
                    message={t('delivery.doneTitle', { date: formatDate(data.deliveredAt) })}
                    description={t('delivery.doneHelp')}
                  />
                ) : (
                  <Card>
                    <Flex vertical gap={8}>
                      <Flex justify="space-between" align="center" gap={8} wrap>
                        <Text strong>{t('delivery.progress', { done, total })}</Text>
                        {data.pdiOutstanding.length === 0 ? (
                          <Tag color="success">{t('delivery.readyToHand')}</Tag>
                        ) : (
                          <Text type="secondary">{t('delivery.notReady')}</Text>
                        )}
                      </Flex>
                      <Progress
                        percent={total === 0 ? 0 : Math.round((done / total) * 100)}
                        status={data.pdiOutstanding.length === 0 ? 'success' : 'active'}
                      />
                      {/* The gate is "somebody looked at every check", not "every
                          check passed" — a customer may knowingly accept a mark. */}
                      <Text type="secondary">{t('delivery.gateHelp')}</Text>
                    </Flex>
                  </Card>
                )}

                <Card styles={{ body: { padding: 0 } }}>
                  <Table<PdiItem>
                    dataSource={data.pdiItems}
                    rowKey={(row) => row.uid}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: t('delivery.fields.check'),
                        dataIndex: 'label',
                        render: (label: string, row: PdiItem) => (
                          <Flex vertical gap={2}>
                            <Text>{label}</Text>
                            {row.note !== null && (
                              <Text
                                type={row.result === 'FAIL' ? 'danger' : 'secondary'}
                                style={{ fontSize: 12 }}
                              >
                                {row.note}
                              </Text>
                            )}
                          </Flex>
                        ),
                      },
                      {
                        title: t('delivery.fields.result'),
                        dataIndex: 'result',
                        width: 260,
                        render: (result: PdiResult | null, row: PdiItem) =>
                          delivered ? (
                            result === null ? (
                              <Text type="secondary">—</Text>
                            ) : (
                              <Tag color={RESULT_TONE[result]}>{t(`delivery.result.${result}`)}</Tag>
                            )
                          ) : (
                            // Three large targets, not a dropdown. This is used
                            // standing next to a motorcycle, one-handed.
                            <Radio.Group
                              value={result}
                              size="large"
                              optionType="button"
                              buttonStyle="solid"
                              onChange={(e) => {
                                record(row, e.target.value as PdiResult);
                              }}
                            >
                              <Radio.Button value="PASS">
                                <CheckCircleOutlined /> {t('delivery.result.PASS')}
                              </Radio.Button>
                              <Radio.Button value="FAIL">
                                <CloseOutlined /> {t('delivery.result.FAIL')}
                              </Radio.Button>
                              <Radio.Button value="NA">
                                <MinusOutlined /> {t('delivery.result.NA')}
                              </Radio.Button>
                            </Radio.Group>
                          ),
                      },
                    ]}
                  />
                </Card>
              </Flex>
            ),
          },
          {
            key: 'overview',
            label: t('sales.tabs.overview'),
            content: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('vehicles.fields.chassisNo')}>
                    <Text strong>{data.chassisNo}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.plateNo')}>
                    {orDash(data.plateNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.model')}>{orDash(data.model)}</Descriptions.Item>
                  <Descriptions.Item label={t('bookings.fields.bookingNo')}>
                    {orDash(data.bookingNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('delivery.fields.scheduled')}>
                    {formatDateTime(data.scheduledAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('delivery.fields.delivered')}>
                    {formatDateTime(data.deliveredAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('delivery.fields.odometer')}>
                    {data.odometer === null ? '—' : t('delivery.km', { count: data.odometer })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('delivery.fields.ack')}>
                    {data.handoverAckType === null
                      ? '—'
                      : `${t(`delivery.ack.${data.handoverAckType}`)} · ${orDash(data.handoverAckRef)}`}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fields.notes')} span={2}>
                    {orDash(data.notes)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
        ]}
      />

      <ActionDrawer
        open={noting !== null}
        title={noting?.item.label ?? ''}
        submitLabel={t('common.save')}
        submitting={check.isPending}
        submitDisabled={note.trim().length < 3}
        onClose={() => {
          setNoting(null);
        }}
        onSubmit={() => {
          if (noting === null) return;
          check.mutate({ itemUid: noting.item.uid, result: noting.result, note: note.trim() });
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label={
              noting?.result === 'FAIL'
                ? t('delivery.fields.whatIsWrong')
                : t('delivery.fields.whyNotApplicable')
            }
            required
            help={t('delivery.noteHelp')}
          >
            <Input.TextArea
              rows={3}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              placeholder={t('delivery.notePlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={handingOver}
        title={t('delivery.handoverTitle')}
        submitLabel={t('delivery.handoverSubmit')}
        submitting={hand.isPending}
        submitDisabled={ackRef.trim().length === 0}
        onClose={() => {
          setHandingOver(false);
        }}
        onSubmit={() => {
          hand.mutate();
        }}
        // Irreversible, and the customer rides away on it.
        consequence={t('delivery.handoverConsequence', {
          chassis: data.chassisNo,
          name: data.customerName,
        })}
      >
        <Form layout="vertical">
          {data.pdiFailed.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('delivery.failedWarning', { count: data.pdiFailed.length })}
              description={data.pdiFailed.join(' · ')}
            />
          )}
          <Form.Item label={t('delivery.fields.ack')} required>
            <Radio.Group
              value={ackType}
              onChange={(e) => {
                setAckType(e.target.value as HandoverAckType);
              }}
              optionType="button"
              buttonStyle="solid"
              size="large"
              options={[
                { value: 'SIGNATURE', label: t('delivery.ack.SIGNATURE') },
                { value: 'OTP', label: t('delivery.ack.OTP') },
                { value: 'PHOTO', label: t('delivery.ack.PHOTO') },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('delivery.fields.ackRef')} required help={t('delivery.ackRefHelp')}>
            <Input
              size="large"
              value={ackRef}
              onChange={(e) => {
                setAckRef(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label={t('delivery.fields.odometer')} help={t('delivery.odometerHelp')}>
            <InputNumber
              min={0}
              max={999999}
              value={odometer}
              onChange={setOdometer}
              suffix="km"
              size="large"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </>
  );
}
